import React, { useEffect, useMemo, useState } from 'react';
import { breakpoints, layout } from '@citrite/web-ui-component';
import styled from '@emotion/styled';
import { DetailedDesktopTileRender } from 'App/Tile/DetailedDesktopTileRender';
import { ResourceTileRenderer } from 'App/Tile/ResourceTileRenderer';
import { useClientWidth } from 'Components/AppLayout/useClientWidth';
import { Resource } from 'Workspace/ResourceProvider/resourceTypes';

export interface Props {
	resources: Resource[];
}

const BREAKPOINT_6_COLUMNS = 1104;
const BREAKPOINT_5_COLUMNS = 924;
const GRID_4_COLUMNS = 4;
const GRID_5_COLUMNS = 5;
const GRID_6_COLUMNS = 6;

function getColumns(width: number) {
	if (width >= BREAKPOINT_6_COLUMNS) {
		return GRID_6_COLUMNS;
	}
	if (width >= BREAKPOINT_5_COLUMNS) {
		return GRID_5_COLUMNS;
	}
	return GRID_4_COLUMNS;
}

const useGetColumns = () => {
	const width = useClientWidth();
	const [columns, setColumns] = useState(getColumns(width));

	useEffect(() => {
		setColumns(getColumns(width));
	}, [width]);

	return columns;
};

const DesktopTileWrapper = styled.div<{ columns: number; isLastDesktop: boolean }>`
	grid-column: span 2;

	${({ columns, isLastDesktop }) => {
		// 5-col grid: span 5 for all desktops except the last odd one which spans 4
		if (columns === GRID_5_COLUMNS) {
			const span = isLastDesktop ? 4 : 5;
			return `
        grid-column: span ${span};
        grid-row: span 2;
      `;
		}

		return `
        grid-column: span 2;
        grid-row: span 2;
      `;
	}}
`;

const ResourceListGridContainer = styled.div<{
	columns: number;
}>`
	max-width: ${breakpoints.centerContentWidthPX}px;
	margin: 0 auto;
	display: grid;
	grid-template-columns: ${({ columns }) =>
		`repeat(${columns === GRID_5_COLUMNS ? 10 : columns}, 1fr)`};
	gap: ${layout.space16};
`;

const AppTileWrapper = styled.div<{ columns: number }>`
	${({ columns }) => {
		if (columns === GRID_5_COLUMNS) {
			return `
        grid-column: span 2;
      `;
		}
		return '';
	}}
`;

export const ResourceListDetailedView = ({ resources }: Props) => {
	const columns = useGetColumns();

	const { desktops, apps } = useMemo(
		() =>
			resources.reduce<{ desktops: Resource[]; apps: Resource[] }>(
				(acc, resource) => {
					if (resource.isdesktop) {
						acc.desktops.push(resource);
					} else {
						acc.apps.push(resource);
					}
					return acc;
				},
				{ desktops: [], apps: [] }
			),
		[resources]
	);

	const isLastDesktopIndex = useMemo(() => {
		if (columns === GRID_5_COLUMNS && desktops.length % 2 !== 0) {
			return desktops.length - 1;
		}

		return -1;
	}, [desktops.length, columns]);

	return (
		<ResourceListGridContainer columns={columns} data-testid="detailed-resource-list">
			{desktops.map((resource, index) => (
				<DesktopTileWrapper
					key={resource.id}
					columns={columns}
					isLastDesktop={isLastDesktopIndex === index}
				>
					<DetailedDesktopTileRender resource={resource} />
				</DesktopTileWrapper>
			))}

			{apps.map(resource => (
				<AppTileWrapper key={resource.id} columns={columns}>
					<ResourceTileRenderer resource={resource} />
				</AppTileWrapper>
			))}
		</ResourceListGridContainer>
	);
};
